<template>
  <div class="w-full max-w-2xl" :dir="isRtl === true ? 'rtl' : 'ltr'">
    <div class="grid grid-cols-2 w-full -m-2">
      <div v-for="channel in channelList" :key="channel.id" class="p-2">
        <button
          type="button"
          @click="onChannelClick(channel)"
          class="p-4 border rounded-2xl transition-colors flex flex-col justify-between min-h-[7rem] group w-full shadow-sm hover:shadow-md bg-white/90 dark:bg-slate-900/70"
          :class="isChannelSelected(channel.id) === true ? 'border-stone-400 dark:border-stone-500 bg-stone-100 dark:bg-stone-800' : 'border-stone-200 dark:border-stone-700 hover:bg-stone-50 dark:hover:bg-stone-800/70'"
          :style="isRtl === true ? { textAlign: 'right' } : { textAlign: 'left' }"
          :disabled="isChannelEnabled(channel.id) !== true"
        >
          <div>
            <div class="flex items-center mb-2" :class="isRtl === true ? 'flex-row-reverse' : 'flex-row'">
              <span class="w-9 h-9 rounded-xl bg-stone-100 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 flex items-center justify-center" :class="isRtl === true ? 'ml-2' : 'mr-2'">
                <Icon :icon="channel.icon" class="text-stone-500 dark:text-stone-300" width="18" />
              </span>
              <span class="font-medium text-slate-800 dark:text-stone-50">{{ channel.displayName }}</span>
            </div>
            <p class="text-xs text-slate-600 dark:text-slate-400 leading-5">{{ channel.description }}</p>
          </div>
          <div v-if="getChannelBindingLabel(channel.id) !== ''" class="mt-3 text-xs font-medium text-stone-700 dark:text-stone-300 truncate">
            {{ getChannelBindingLabel(channel.id) }}
          </div>
        </button>
      </div>
    </div>

    <WhatsAppNumberPickerModal
      :visible="showWhatsAppModal === true"
      :items="availableWhatsapp"
      :loading="loadingWhatsapp === true"
      :currentId="selectedWhatsappSourceId"
      @close="showWhatsAppModal = false"
      @confirm="onWhatsAppConfirm"
    />

    <WhatsAppNumberPickerModal
      :visible="showPhoneModal === true"
      :items="availablePhone"
      :loading="loadingPhone === true"
      :currentId="selectedPhoneSourceId"
      mode="phone"
      @close="showPhoneModal = false"
      @confirm="onPhoneConfirm"
    />

    <teleport to="body">
      <div v-if="showEmailModal === true" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div class="bg-base-100 dark:bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-md" :dir="isRtl === true ? 'rtl' : 'ltr'">
          <div class="flex items-center mb-4" :class="isRtl === true ? 'flex-row-reverse' : 'flex-row'">
            <span class="inline-flex w-9 h-9 rounded-lg items-center justify-center bg-base-300 dark:bg-gray-700" :class="isRtl === true ? 'ml-3' : 'mr-3'">
              <Icon icon="tabler:mail" class="w-5 h-5 text-base-content" />
            </span>
            <h3 class="text-lg font-semibold">{{ t('deploymentChannels.email.title', 'Email') }}</h3>
          </div>
          <p class="text-sm mb-6">{{ t('deploymentChannels.email.contactSupport', 'please contact support for email setup.') }}</p>
          <div class="flex justify-end">
            <button type="button" class="btn" @click="showEmailModal = false">{{ t('common.close', 'Close') }}</button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import axios from '@/api/axiosInstance';
import { globalState } from '@/globalState';
import { useI18n } from '@/i18n';
import WhatsAppNumberPickerModal from '@/components/WhatsAppNumberPickerModal.vue';

const emit = defineEmits(['submit', 'selection-change']);

const { t, currentLanguage, availableLanguages } = useI18n();

const isRtl = computed(() => {
  const language = availableLanguages.value.find((item) => item.code === currentLanguage.value);
  if (language === undefined || language === null) {
    return false;
  }
  return language.dir === 'rtl';
});

const phoneSourceTypes = ['twilioPhone', 'phonePartner', 'phoneCustomSip', 'sipTrunk', 'sonarixPhone', 'genovisePhone', 'phone'];

const selectedChannels = ref([]);
const selectedWhatsappSourceId = ref(null);
const selectedWhatsappNumber = ref('');
const selectedPhoneSourceId = ref(null);
const selectedPhoneNumber = ref('');

const showWhatsAppModal = ref(false);
const pendingWhatsAppChannel = ref('Whatsapp');
const showPhoneModal = ref(false);
const showEmailModal = ref(false);
const availableWhatsapp = ref([]);
const availablePhone = ref([]);
const loadingWhatsapp = ref(false);
const loadingPhone = ref(false);

const allDeploymentChannels = ['Web', 'Web Voice Agent', 'Whatsapp', 'Whatsapp Voice', 'Phone', 'Email'];

const channelList = computed(() => {
  return [
    {
      id: 'Web',
      displayName: t('deploymentChannels.channels.web.name', 'Web'),
      description: t('deploymentChannels.channels.web.desc', 'Embed or website widget'),
      icon: 'heroicons:globe-alt'
    },
    {
      id: 'Web Voice Agent',
      displayName: t('deploymentChannels.channels.webVoiceAgent.name', 'Web Voice Agent'),
      description: t('deploymentChannels.channels.webVoiceAgent.desc', 'Voice agent in the web chat'),
      icon: 'tabler:microphone'
    },
    {
      id: 'Whatsapp',
      displayName: t('deploymentChannels.channels.whatsapp.name', 'Whatsapp'),
      description: t('deploymentChannels.channels.whatsapp.desc', 'WhatsApp messaging'),
      icon: 'tabler:brand-whatsapp'
    },
    {
      id: 'Whatsapp Voice',
      displayName: t('deploymentChannels.channels.whatsappVoice.name', 'WhatsApp Voice'),
      description: t('deploymentChannels.channels.whatsappVoice.desc', 'AI voice calls through WhatsApp'),
      icon: 'tabler:brand-whatsapp'
    },
    {
      id: 'Phone',
      displayName: t('deploymentChannels.channels.phone.name', 'Phone'),
      description: t('deploymentChannels.channels.phone.desc', 'Phone support'),
      icon: 'tabler:phone'
    },
    {
      id: 'Email',
      displayName: t('deploymentChannels.channels.email.name', 'Email'),
      description: t('deploymentChannels.channels.email.desc', 'Email channel'),
      icon: 'tabler:mail'
    }
  ];
});

const enabledChannels = computed(() => {
  const subscription = typeof globalState.userInfo?.subscription === 'string' ? globalState.userInfo.subscription.trim().toLowerCase() : '';
  if (subscription === 'enterprise') {
    return allDeploymentChannels;
  }
  if (Array.isArray(globalState.orgSettings?.allowedSources) === true) {
    return globalState.orgSettings.allowedSources;
  }
  return allDeploymentChannels;
});

const canContinue = computed(() => {
  return Array.isArray(selectedChannels.value) === true && selectedChannels.value.length > 0;
});

function isChannelEnabled(id) {
  return enabledChannels.value.includes(id) === true;
}

function isChannelSelected(id) {
  return selectedChannels.value.includes(id) === true;
}

function getChannelBindingLabel(id) {
  if ((id === 'Whatsapp' || id === 'Whatsapp Voice') && selectedWhatsappNumber.value !== '') {
    return selectedWhatsappNumber.value;
  }
  if (id === 'Phone' && selectedPhoneNumber.value !== '') {
    return selectedPhoneNumber.value;
  }
  return '';
}

function normalizeSourceId(value) {
  if (value === null || value === undefined) {
    return null;
  }
  const parsed = Number(value);
  if (Number.isFinite(parsed) !== true) {
    return null;
  }
  return Math.floor(parsed);
}

function emitSelectionSummary() {
  const labels = [];
  let index = 0;
  while (index < selectedChannels.value.length) {
    const channelId = selectedChannels.value[index];
    const match = channelList.value.find((item) => item.id === channelId);
    if (match !== undefined) {
      let label = match.displayName;
      const binding = getChannelBindingLabel(channelId);
      if (binding !== '') {
        label = `${label} (${binding})`;
      }
      labels.push(label);
    }
    index = index + 1;
  }
  emit('selection-change', labels.join(', '));
}

function addChannel(id) {
  if (selectedChannels.value.includes(id) !== true) {
    selectedChannels.value = selectedChannels.value.concat([id]);
    emitSelectionSummary();
  }
}

function removeChannel(id) {
  selectedChannels.value = selectedChannels.value.filter((item) => item !== id);
  emitSelectionSummary();
}

async function loadWhatsappNumbers() {
  loadingWhatsapp.value = true;
  try {
    const res = await axios.get(`${globalState.apiEndpoint}/api/communicationSources/available`, { params: { type: 'whatsapp' } });
    const raw = Array.isArray(res.data) === true ? res.data : [];
    availableWhatsapp.value = raw
      .filter((item) => {
        if (pendingWhatsAppChannel.value === 'Whatsapp Voice') {
          return item.type === 'whatsappMeta' && item.voiceEnabled === true;
        }
        return item.type === 'whatsappMeta' || item.type === 'whatsappSmooch' || item.type === 'whatsapp99Digital';
      })
      .map((item) => {
        const sidRaw = item.sourceId !== undefined && item.sourceId !== null ? item.sourceId : (item.sourceid !== undefined && item.sourceid !== null ? item.sourceid : (item.id !== undefined && item.id !== null ? item.id : null));
        const sid = normalizeSourceId(sidRaw);
        return {
          sourceId: sid,
          display: item.display !== undefined && item.display !== null ? item.display : (item.name !== undefined && item.name !== null ? item.name : ''),
          number: item.number !== undefined && item.number !== null ? item.number : ''
        };
      })
      .filter((item) => item.sourceId !== null);
  } catch (error) {
    availableWhatsapp.value = [];
  } finally {
    loadingWhatsapp.value = false;
  }
}

async function loadPhoneNumbers() {
  loadingPhone.value = true;
  try {
    const res = await axios.get(`${globalState.apiEndpoint}/api/communicationSources/available`, { params: { type: 'phoneNumber' } });
    const raw = Array.isArray(res.data) === true ? res.data : [];
    availablePhone.value = raw
      .filter((item) => phoneSourceTypes.includes(String(item.type)) === true)
      .map((item) => {
        const sidRaw = item.sourceId !== undefined && item.sourceId !== null ? item.sourceId : (item.sourceid !== undefined && item.sourceid !== null ? item.sourceid : (item.id !== undefined && item.id !== null ? item.id : null));
        const sid = normalizeSourceId(sidRaw);
        return {
          sourceId: sid,
          display: item.display !== undefined && item.display !== null ? item.display : (item.name !== undefined && item.name !== null ? item.name : ''),
          number: item.number !== undefined && item.number !== null ? item.number : ''
        };
      })
      .filter((item) => item.sourceId !== null);
  } catch (error) {
    availablePhone.value = [];
  } finally {
    loadingPhone.value = false;
  }
}

async function onChannelClick(channel) {
  if (isChannelEnabled(channel.id) !== true) {
    return;
  }
  if (channel.id === 'Whatsapp' || channel.id === 'Whatsapp Voice') {
    if (isChannelSelected(channel.id) === true) {
      removeChannel(channel.id);
      if (isChannelSelected('Whatsapp') !== true && isChannelSelected('Whatsapp Voice') !== true) {
        selectedWhatsappSourceId.value = null;
        selectedWhatsappNumber.value = '';
      }
      return;
    }
    pendingWhatsAppChannel.value = channel.id;
    showWhatsAppModal.value = true;
    await loadWhatsappNumbers();
    return;
  }
  if (channel.id === 'Phone') {
    if (isChannelSelected('Phone') === true) {
      removeChannel('Phone');
      selectedPhoneSourceId.value = null;
      selectedPhoneNumber.value = '';
      return;
    }
    showPhoneModal.value = true;
    await loadPhoneNumbers();
    return;
  }
  if (channel.id === 'Email') {
    showEmailModal.value = true;
    return;
  }
  if (isChannelSelected(channel.id) === true) {
    removeChannel(channel.id);
  } else {
    addChannel(channel.id);
  }
}

function onWhatsAppConfirm(payload) {
  if (payload === null || payload === undefined) {
    showWhatsAppModal.value = false;
    return;
  }
  const sid = normalizeSourceId(payload.sourceId);
  selectedWhatsappSourceId.value = sid;
  if (typeof payload.number === 'string' && payload.number.length > 0) {
    selectedWhatsappNumber.value = payload.number;
  }
  addChannel(pendingWhatsAppChannel.value);
  showWhatsAppModal.value = false;
}

function onPhoneConfirm(payload) {
  if (payload === null || payload === undefined) {
    showPhoneModal.value = false;
    return;
  }
  const sid = normalizeSourceId(payload.sourceId);
  selectedPhoneSourceId.value = sid;
  if (typeof payload.number === 'string' && payload.number.length > 0) {
    selectedPhoneNumber.value = payload.number;
  }
  addChannel('Phone');
  showPhoneModal.value = false;
}

function submit() {
  if (canContinue.value === false) {
    return;
  }
  emit('submit', {
    channels: selectedChannels.value.slice(),
    whatsappSourceId: selectedWhatsappSourceId.value,
    whatsappNumber: selectedWhatsappNumber.value,
    phoneSourceId: selectedPhoneSourceId.value,
    phoneNumber: selectedPhoneNumber.value
  });
}

onMounted(() => {
  if (selectedChannels.value.length === 0) {
    if (isChannelEnabled('Web') === true) {
      addChannel('Web');
    }
  } else {
    emitSelectionSummary();
  }
});

defineExpose({
  submit,
  isReady: () => Array.isArray(selectedChannels.value) === true && selectedChannels.value.length > 0
});
</script>
